import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  MenuItem,
} from '@mui/material'
import { Save as SaveIcon, ArrowBack as BackIcon } from '@mui/icons-material'
import { request } from '../common/services/request'
import { API_ENDPOINTS } from '../common/endpoints'
import { getPatientDetailRoute } from '../common/constant/routes'
import type { PatientCreate, Patient } from '../common/types'

function PatientCreatePage() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [useFakeDefaults, setUseFakeDefaults] = useState(true)
  const [syncToOpenemr, setSyncToOpenemr] = useState(false)

  const [formData, setFormData] = useState<PatientCreate>({
    fname: '',
    lname: '',
    dob: '',
    sex: '',
    email: '',
    title: '',
    street: '',
    city: '',
    state: '',
    postal_code: '',
    phone_home: '',
  })

  const handleChange = (field: keyof PatientCreate) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [field]: e.target.value })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      const client = request()
      const payload: PatientCreate = {
        ...formData,
        use_fake_defaults: useFakeDefaults,
        sync_to_openemr: syncToOpenemr,
      }

      // Remove empty fields
      Object.keys(payload).forEach((key) => {
        const k = key as keyof PatientCreate
        if (payload[k] === '' || payload[k] === undefined) {
          delete payload[k]
        }
      })

      const res = await client.post<Patient>(API_ENDPOINTS.PATIENTS.BASE, payload)
      
      // Ensure we have a valid UUID before navigating
      const patientUuid = res.data.uuid
      if (!patientUuid) {
        throw new Error('Patient created but no UUID returned')
      }
      
      navigate(getPatientDetailRoute(patientUuid))
    } catch (e: any) {
      const message = e.response?.data?.detail || e.message || 'Failed to create patient'
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={2} mb={2}>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/patients')}>
          Back
        </Button>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Create Patient
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Quick Options
            </Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={useFakeDefaults}
                      onChange={(e) => setUseFakeDefaults(e.target.checked)}
                    />
                  }
                  label="Use Faker for empty fields"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={syncToOpenemr}
                      onChange={(e) => setSyncToOpenemr(e.target.checked)}
                    />
                  }
                  label="Sync to OpenEMR"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mb: 1.5 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={2}>
                <TextField
                  select
                  fullWidth
                  label="Title"
                  value={formData.title}
                  onChange={handleChange('title')}
                >
                  <MenuItem value="">-</MenuItem>
                  <MenuItem value="Mr.">Mr.</MenuItem>
                  <MenuItem value="Mrs.">Mrs.</MenuItem>
                  <MenuItem value="Ms.">Ms.</MenuItem>
                  <MenuItem value="Dr.">Dr.</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={5}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={formData.fname}
                  onChange={handleChange('fname')}
                  placeholder={useFakeDefaults ? 'Auto-generated' : 'Required'}
                />
              </Grid>
              <Grid item xs={12} sm={5}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={formData.lname}
                  onChange={handleChange('lname')}
                  placeholder={useFakeDefaults ? 'Auto-generated' : 'Required'}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Date of Birth"
                  value={formData.dob}
                  onChange={handleChange('dob')}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Sex"
                  value={formData.sex}
                  onChange={handleChange('sex')}
                >
                  <MenuItem value="">-</MenuItem>
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="email"
                  label="Email"
                  value={formData.email}
                  onChange={handleChange('email')}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={formData.phone_home}
                  onChange={handleChange('phone_home')}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mb: 1.5 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Address
            </Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Street"
                  value={formData.street}
                  onChange={handleChange('street')}
                />
              </Grid>
              <Grid item xs={12} sm={5}>
                <TextField
                  fullWidth
                  label="City"
                  value={formData.city}
                  onChange={handleChange('city')}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="State"
                  value={formData.state}
                  onChange={handleChange('state')}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Postal Code"
                  value={formData.postal_code}
                  onChange={handleChange('postal_code')}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Box display="flex" gap={2}>
          <Button
            type="submit"
            variant="contained"
            size="large"
            startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Create Patient'}
          </Button>
          <Button variant="outlined" onClick={() => navigate('/patients')} disabled={loading}>
            Cancel
          </Button>
        </Box>
      </form>
    </Box>
  )
}

export default PatientCreatePage
